import { PauseIcon, WarningIcon } from '@phosphor-icons/react'
import { Link } from 'react-router-dom'
import { Button } from 'app/components/ui/button'
import { cn } from 'app/lib/utils'
import { RUN_REASON_TEXT } from './runViz'

type FixLink = { label: string; href: (locationId?: string) => string | undefined }

const LOCATION_SETTINGS: FixLink = {
  label: `Location settings`,
  href: (locationId) => locationId && `/settings/location-settings/${locationId}`,
}

/** Where the fix for a stop lives, when it is a page rather than the step itself. */
const FIX_LINK: Readonly<Record<string, FixLink>> = {
  location_lost_inbox: LOCATION_SETTINGS,
  location_lost_sms_agent: LOCATION_SETTINGS,
  no_texting_number: LOCATION_SETTINGS,
  location_not_configured: LOCATION_SETTINGS,
  domain_not_verified: { label: `Sending domains`, href: () => `/settings/sending-domains` },
}

type WorkflowStoppedBannerProps = {
  /** `error`: the engine stopped it because a step can't run. `paused`: held with no reason to fix. */
  hold: `error` | `paused`
  /** The engine's reason code, e.g. `location_lost_inbox`. */
  reason?: string | null
  /** The broken step's label, absent when the step is no longer on the canvas. */
  stepLabel?: string
  stoppedAt?: string | null
  /** Open runs held where they stopped. Undefined while it loads. */
  heldCount?: number
  locationId?: string
  onShowStep?: () => void
}

/** Above the canvas of a held workflow: who is held and since when, and for an error, which step
 *  broke and why. */
export function WorkflowStoppedBanner({
  hold,
  reason,
  stepLabel,
  stoppedAt,
  heldCount,
  locationId,
  onShowStep,
}: WorkflowStoppedBannerProps) {
  const error = hold === `error`
  const code = reason?.toLowerCase() ?? ``
  const why = RUN_REASON_TEXT[code] ?? `this step can't run`
  const fix = FIX_LINK[code]
  const fixHref = fix?.href(locationId)
  const since = stoppedAt
    ? new Date(stoppedAt).toLocaleString(`en-US`, {
        month: `short`,
        day: `numeric`,
        hour: `numeric`,
        minute: `2-digit`,
      })
    : undefined

  return (
    <div
      role="alert"
      data-testid="workflow-stopped-banner"
      className={cn(
        `mx-3 mt-3 rounded-lg border px-3.5 py-3`,
        error ? `border-danger-subtle bg-danger-minimal` : `border-warning-subtle bg-warning-minimal`,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className={cn(
              `flex items-center gap-1.5 text-sm font-semibold`,
              error ? `text-danger-strong` : `text-warning-strong`,
            )}
          >
            {error ? (
              <WarningIcon size={15} weight="bold" aria-hidden />
            ) : (
              <PauseIcon size={15} weight="bold" aria-hidden />
            )}
            {!error ? `Paused` : stepLabel ? `Stopped: "${stepLabel}" can't run` : `Stopped: a step can't run`}
          </p>
          {error && (
            <p data-testid="workflow-stopped-reason" className="mt-1 text-sm text-default">
              {why.charAt(0).toUpperCase() + why.slice(1)}.
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-1.5">
          {error && onShowStep && (
            <Button variant="white" size="xs" onClick={onShowStep} data-testid="workflow-stopped-show-step">
              Show step
            </Button>
          )}
          {error && fix && fixHref && (
            <Button asChild variant="primary" size="xs">
              <Link to={fixHref}>{fix.label}</Link>
            </Button>
          )}
        </div>
      </div>
      <div
        className={cn(
          `mt-2.5 flex flex-wrap gap-x-4 gap-y-1 border-t pt-2 text-xs`,
          error ? `border-danger-subtle text-danger-strong` : `border-warning-subtle text-warning-strong`,
        )}
      >
        {heldCount !== undefined && (
          <span>
            {heldCount} {heldCount === 1 ? `patient` : `patients`} held
          </span>
        )}
        <span>No new enrollments</span>
        {since && <span>Since {since}</span>}
      </div>
    </div>
  )
}
